/**
 * Signed-in card (under the navbar) for pending organizer invitations. The email link lands on
 * /settings/account?organizer-invite=<id>; the matching invite is listed first. Accept creates the
 * team and starts the trial; Decline tells the inviting admin.
 */

"use client";

import React, { useCallback, useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Alert, Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { useAuth } from "../../util/auth";
import { fetchMyOrganizerInvites, organizerAcceptInvite, organizerDeclineInvite } from "../../api/stripe";
import { organizerInviteErrorMessage } from "../../util/organizerInviteErrors";
import { TEAM_INVITE_SETTINGS_PATH, TEAM_MEMBERSHIP_CHANGED_EVENT } from "../../util/teamMembershipEvents";

export const ORGANIZER_INVITE_QUERY_PARAM = "organizer-invite";

const WRAP = { overflowWrap: "anywhere", minWidth: 0 };

/** Hidden on auth screens only; Account is where the email link lands. */
export function shouldShowOrganizerInviteCard(pathname) {
  return !(typeof pathname === "string" ? pathname : "").startsWith("/auth");
}

export function mapOrganizerInvitesForMe(raw, focusId = null) {
  const results = Array.isArray(raw?.results) ? raw.results : [];
  const rows = results
    .filter((r) => r && typeof r.invite_id === "string" && r.invite_id !== "")
    .map((r) => ({
      invite_id: r.invite_id,
      team_display_name: String(r.team_display_name ?? ""),
      seat_count: Number(r.seat_count ?? 0),
      duration_weeks: Number(r.duration_weeks ?? 0),
      accept_by: r.accept_by ? String(r.accept_by) : null,
      note: r.note ? String(r.note) : "",
      invited_by_display_name: r.invited_by_display_name ? String(r.invited_by_display_name) : "",
    }));
  return focusId ? [...rows].sort((a, b) => Number(b.invite_id === focusId) - Number(a.invite_id === focusId)) : rows;
}

function plural(count, word) {
  return `${count.toLocaleString()} ${word}${count === 1 ? "" : "s"}`;
}

function formatDate(iso) {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { dateStyle: "medium" }) : null;
}

export default function OrganizerInviteCard() {
  const { user } = useAuth();
  const accessToken = user?.accessToken ?? null;
  const pathname = usePathname();
  const router = useRouter();
  const [invites, setInvites] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken) {
      setInvites([]);
      return;
    }
    try {
      const focusId =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get(ORGANIZER_INVITE_QUERY_PARAM)
          : null;
      setInvites(mapOrganizerInvitesForMe(await fetchMyOrganizerInvites(accessToken), focusId));
    } catch (err) {
      console.error("[OrganizerInviteCard] Could not load organizer invites:", err);
      setInvites([]);
    }
  }, [accessToken]);

  useEffect(() => {
    void load();
  }, [load, pathname]);

  const respond = useCallback(
    async (invite, accept) => {
      setBusyId(invite.invite_id);
      setError(null);
      try {
        if (accept) {
          const result = await organizerAcceptInvite(accessToken, invite.invite_id);
          setAccepted({ team: invite.team_display_name, expires_at: result?.expires_at ?? null });
        } else {
          await organizerDeclineInvite(accessToken, invite.invite_id);
        }
        setInvites((prev) => prev.filter((i) => i.invite_id !== invite.invite_id));
        window.dispatchEvent(new CustomEvent(TEAM_MEMBERSHIP_CHANGED_EVENT));
      } catch (err) {
        setError(
          organizerInviteErrorMessage(err, {
            context: "OrganizerInviteCard",
            fallback: accept
              ? "Could not accept the invitation. Try again in a moment."
              : "Could not decline the invitation. Try again in a moment.",
          })
        );
        void load();
      } finally {
        setBusyId(null);
      }
    },
    [accessToken, load]
  );

  if (!accessToken || !shouldShowOrganizerInviteCard(pathname)) {
    return null;
  }
  if (invites.length === 0 && !accepted && !error) {
    return null;
  }

  const endDate = formatDate(accepted?.expires_at);

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", px: 2, pt: 2 }}>
      <Stack spacing={1.5}>
        {accepted && (
          <Alert
            severity="success"
            onClose={() => setAccepted(null)}
            action={
              <Tooltip title="Opens Account, where Team Admin shows your getting-started checklist.">
                <Button color="inherit" size="small" onClick={() => router.push(TEAM_INVITE_SETTINGS_PATH)}>
                  Open Team Admin
                </Button>
              </Tooltip>
            }
          >
            <span style={WRAP}>{accepted.team}</span> is ready.
            {endDate ? ` Your trial runs until ${endDate}.` : ""}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {invites.map((invite) => {
          const acceptBy = formatDate(invite.accept_by);
          const busy = busyId === invite.invite_id;
          return (
            <Box
              key={invite.invite_id}
              component="section"
              aria-label={`Organizer invitation for ${invite.team_display_name}`}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
                justifyContent: "space-between",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
                p: 2,
              }}
            >
              <Box sx={{ flex: "1 1 320px", ...WRAP }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, ...WRAP }}>
                  {invite.invited_by_display_name
                    ? `${invite.invited_by_display_name} invited you to organize ${invite.team_display_name}`
                    : `You are invited to organize ${invite.team_display_name}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {plural(invite.seat_count, "seat")} for {plural(invite.duration_weeks, "week")}, counted from the
                  day you accept.
                  {acceptBy ? ` Accept by ${acceptBy}.` : ""}
                </Typography>
                {invite.note && (
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-line", ...WRAP }}>
                    {invite.note}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Tooltip title="Creates the team and starts the trial now. You become its team admin.">
                  <span>
                    <Button variant="contained" size="small" disabled={busy} onClick={() => void respond(invite, true)}>
                      Accept
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Turns the invitation down and tells the admin who sent it. No team is created.">
                  <span>
                    <Button variant="outlined" size="small" disabled={busy} onClick={() => void respond(invite, false)}>
                      Decline
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
